import axios from "axios";
import { getApiBaseUrl } from "./getApiBaseUrl";
import { ngrokSkipBrowserWarningHeaders } from "./ngrokDevHeaders";
import { clearLoginAdminHint } from "../utils/loginAdminHint";

// Create axios instance with credentials (cookies)
const api = axios.create({
  baseURL: "",
  /**
   * 0 = fără timeout client (axios). Evită ECONNABORTED la 30–60s când serverul
   * răspunde lent (DB, bcrypt, VPN) sau proxy-ul întârzie.
   */
  timeout: 0,
  withCredentials: true,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
  },
});

api.interceptors.request.use((config) => {
  config.baseURL = getApiBaseUrl();
  const ngrok = ngrokSkipBrowserWarningHeaders();
  for (const [k, v] of Object.entries(ngrok)) {
    config.headers[k] = v;
  }
  return config;
});

/** Răspuns API: cont cu drepturi de admin (tolerant la serializări exotice). */
export function isAdminFromApi(value: unknown): boolean {
  return value === true || value === "true" || value === 1;
}

/**
 * Extrage flag admin din GET /me sau din `{ is_admin }` (snake_case / camelCase).
 * Unele proxy-uri sau serializări pot expune `isAdmin` în loc de `is_admin`.
 */
export function adminFlagFromPayload(data: unknown): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }
  const o = data as Record<string, unknown>;
  const v = o.is_admin ?? o.isAdmin;
  return isAdminFromApi(v);
}

// Types
export interface LoginResponse {
  message: string;
  /** Setat la login ca să nu fie nevoie de al doilea request GET /me/is-admin. */
  is_admin?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  nume_complet: string;
}

export interface UserInfo {
  /** UUID din API (string JSON) */
  id_utilizator: string;
  email: string;
  nume_complet: string | null;
  activ: boolean;
  email_verificat: boolean;
  termeni_acceptati: boolean;
  id_vehicul_selectat: string | null;
  data_creare: string;
  /** Din GET /me — evită al doilea request la /me/is-admin. */
  is_admin?: boolean;
  /**
   * Firme unde CUI-ul a fost revocat administrativ — clientul trebuie să ceară un CUI nou
   * înainte de a continua (vezi CuiRevokedBarrier).
   */
  firme_cu_cui_revocat?: { id_firma: string; nume_firma: string }[];
}

// Auth API functions
export const authAPI = {
  /**
   * Login with email and password.
   * The server sets an httpOnly cookie with the JWT token.
   */
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const formData = new FormData();
    formData.append("username", email); // Backend expects 'username' field
    formData.append("password", password);
    const response = await api.post<LoginResponse>(
      "/api/v1/auth/login",
      formData,
    );
    return response.data;
  },

  /**
   * Register a new user
   */
  register: async (data: RegisterData): Promise<UserInfo> => {
    const response = await api.post<UserInfo>("/api/v1/auth/register", data);
    return response.data;
  },

  /**
   * Get current user info.
   * Uses the httpOnly cookie automatically.
   */
  getMe: async (): Promise<UserInfo> => {
    // În dev, forțează URL unic — unele proxy/cache (sau extensii) păstrau vechiul JSON /me.
    const bust =
      import.meta.env.DEV && typeof window !== "undefined"
        ? `?_=${Date.now()}`
        : "";
    const response = await api.get<UserInfo>(`/api/v1/auth/me${bust}`);
    return response.data;
  },

  /**
   * Drepturi admin din GET /me/is-admin (dict simplu — evită pierderea câmpului pe /me).
   */
  isAdmin: async (): Promise<boolean> => {
    try {
      const bust =
        import.meta.env.DEV && typeof window !== "undefined"
          ? `?_=${Date.now()}`
          : "";
      const response = await api.get<{ is_admin?: boolean; isAdmin?: boolean }>(
        `/api/v1/auth/me/is-admin${bust}`,
      );
      return adminFlagFromPayload(response.data);
    } catch {
      return false;
    }
  },

  /**
   * Logout — clears the httpOnly auth cookies on the server.
   */
  logout: async (): Promise<void> => {
    try {
      await api.post("/api/v1/auth/logout");
    } catch {
      // Ignore errors — cookies may already be gone
    }
    clearLoginAdminHint();
  },

  /**
   * Refresh the access token using the refresh cookie.
   */
  refresh: async (): Promise<void> => {
    await api.post("/api/v1/auth/refresh");
  },

  /**
   * Initiate Google OAuth login.
   * Redirects to Google's OAuth consent page.
   */
  googleLogin: (): void => {
    const path = "/api/v1/auth/google";
    const base = getApiBaseUrl();
    window.location.href = base === "" ? path : `${base}${path}`;
  },

  /**
   * Verify email address using the token from the verification email.
   */
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      "/api/v1/auth/verify-email",
      { token },
    );
    return response.data;
  },

  /**
   * Resend the verification email for an unverified account.
   */
  resendVerification: async (email: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      "/api/v1/auth/resend-verification",
      { email },
    );
    return response.data;
  },

  /**
   * Request a password reset email.
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      "/api/v1/auth/forgot-password",
      { email },
    );
    return response.data;
  },

  /**
   * Reset password using a token from the reset email.
   */
  resetPassword: async (
    token: string,
    password: string,
  ): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      "/api/v1/auth/reset-password",
      { token, password },
    );
    return response.data;
  },

  /**
   * Accept terms and conditions (one-way, cannot be reverted).
   */
  acceptTerms: async (): Promise<{ termeni_acceptati: boolean }> => {
    const response = await api.patch<{ termeni_acceptati: boolean }>(
      "/api/v1/auth/me/accept-terms",
    );
    return response.data;
  },
};

export default authAPI;
